import React, { useEffect, useState } from 'react';
import { PageLayout } from '../../layout';
import { Box, Button, Modal, Tooltip, Typography } from '@mui/material';
import RegisteredVisitors from './RegisteredVisitors';
import ScheduleMenu from '../../People/menu';
import { useFirebase } from '../../Firebase';
import { useAuth } from '../../../providers';
import DateSelector from '../../layout/DateSelector';
import { DateToday } from '../../../constants/functions';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import NewVisitorForm from '../../Interactions/newVisitorForm';
import EditVisitor from './editVisitor';
import ExportButton from '../../Reporting/exportButton';

const Main = () => {
   const firebase = useFirebase();
   const { authUser } = useAuth();
   const today = DateToday();
   const [location, setLocation] = useState(null);
   const [displayFilter, setDisplayFilter] = useState(null);
   const [loading, setLoading] = useState(true);
   const [date, setDate] = useState(today);
   const [collapsed, setCollapsed] = useState(false);
   const [editModal, setEditModal] = useState(false);
   const [visitor, setVisitor] = useState(null);
   const [visitorExport, setVisitorExport] = useState(null);

   async function getPrimaryLocation() {
      try {
         const result = await firebase.location(authUser.lid[0]).get();
         if (result.exists) {
            setDisplayFilter({ title: result.data().name, id: result.id });
         }
      } catch (err) {
         console.error('getPrimaryLocation', err);
      }
   }

   async function getDisplayFilter() {
      if (!displayFilter) return;
      try {
         const result = await firebase.location(displayFilter.id).get();
         setLocation({ ...result.data(), lid: result.id, cid: authUser.cid });
         setLoading(false);
      } catch (err) {
         console.error('getDisplayFilter', err);
         setLoading(false);
      }
   }

   function handleClose() {
      setCollapsed(false);
   }

   useEffect(getPrimaryLocation, []);
   useEffect(getDisplayFilter, [displayFilter]);

   useEffect(() => {
      if (location && date && authUser?.roles?.ADMIN) {
         const getVisitors = async () => {
            const result = await firebase.findHost(displayFilter.id, date.ref).get();
            let data = [];
            if (result.exists) {
               Object.entries(result.data()).map(([key, value]) => {
                  if (key !== 'visitorNo') {
                     data.push({
                        visitorName: value.name,
                        visitorEmail: key,
                        unplanned: value.unplanned,
                        hostName: value.host.name,
                        hostEmail: value.host.email,
                        arrivalTime:
                           value.arrivalTime && value.arrivalTime.toDate
                              ? value.arrivalTime.toDate().toLocaleString()
                              : '',
                        departureTime:
                           value.departureTime && value.departureTime.toDate
                              ? value.departureTime.toDate().toLocaleString()
                              : '',
                        checkin:
                           value.checkin && value.checkin.toDate
                              ? value.checkin.toDate().toLocaleString()
                              : '',
                        checkout:
                           value.checkout && value.checkout.toDate
                              ? value.checkout.toDate().toLocaleString()
                              : '',
                     });
                  }
               });
            } else {
               data.push({
                  visitorName: '',
                  visitorEmail: '',
                  unplanned: '',
                  hostName: '',
                  hostEmail: '',
                  arrivalTime: '',
                  departureTime: '',
                  checkin: '',
                  checkout: '',
               });
            }
            setVisitorExport(data);
         };
         getVisitors();
      }
   }, [location, date, authUser]);

   if (loading) {
      return null;
   }
   function handleEditModal(data) {
      setEditModal(true);
      setVisitor(data);
   }

   function closeEditModal() {
      setEditModal(false);
      setVisitor(null);
   }

   return (
      <PageLayout>
         <Modal
            open={collapsed}
            onClose={handleClose}
            sx={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
            }}
            disableEnforceFocus
            aria-labelledby="modal-modal-title"
         >
            <Box>
               <NewVisitorForm
                  cid={authUser.cid}
                  lid={displayFilter.id}
                  date={date.ref}
                  dayVisitors={[]}
                  setCollapsed={setCollapsed}
               />
            </Box>
         </Modal>
         <Modal
            open={editModal}
            onClose={() => closeEditModal}
            sx={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
            }}
            disableEnforceFocus
            aria-labelledby="modal-modal-titled"
         >
            <Box>
               <EditVisitor
                  date={date}
                  cid={authUser.cid}
                  lid={displayFilter.id}
                  visitor={visitor}
                  handleEditModal={handleEditModal}
                  closeEditModal={closeEditModal}
               />
            </Box>
         </Modal>
         <Box
            sx={{
               width: '100%',
               display: 'flex',
               flexDirection: 'row',
               justifyContent: 'space-between',
            }}
         >
            <Box sx={{ width: '33%' }}>
               <ScheduleMenu
                  displayFilter={displayFilter}
                  setDisplayFilter={setDisplayFilter}
                  featureFilter="visitors"
               />
            </Box>
            <Box sx={{ width: '33%' }}>
               <DateSelector date={date} setDate={setDate} />
            </Box>
            <Box
               sx={{
                  width: '33%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: authUser?.roles?.ADMIN ? 'space-between' : 'flex-end',
               }}
            >
               {location?.visitorsEnabled && authUser?.roles?.ADMIN &&
                  visitorExport != null && (
                  <ExportButton
                     data={visitorExport}
                     headers={[
                        'visitorName',
                        'visitorEmail',
                        'unplanned',
                        'hostName',
                        'hostEmail',
                        'scheduleArrivalTime',
                        'scheduledDepartureTime',
                        'checkIn',
                        'checkOut',
                     ]}
                     time={true}
                     icon={true}
                  />
               )}
               {location?.visitorsEnabled && (
                  <Button
                     fullWidth
                     variant="outlined"
                     sx={{ maxWidth: '250px' }}
                     onClick={() => setCollapsed(true)}
                  >
                     <PersonAddIcon sx={{ mr: 1 }} />
                     Add Visitor
                  </Button>
               )}
            </Box>
         </Box>
         {location?.visitorsEnabled ? (
            <RegisteredVisitors
               displayFilter={displayFilter}
               date={date}
               handleEditModal={handleEditModal}
            />
         ) : (
            <Box sx={{ mt: 6, p: 4, textAlign: 'center' }}>
               <Typography variant="h5" gutterBottom>
                  Visitor Management is unavailable for this location
               </Typography>
               <Typography variant="body1" color="text.secondary">
                  Use the location selector above to switch to a location with Visitor
                  Management enabled.
               </Typography>
            </Box>
         )}
      </PageLayout>
   );
};

export default Main;
